const PENDING_EVENT_LIMIT = 50;

let amplitude = null;
let analyticsEnabled = false;
let initPromise = null;
let pendingIdentify = null;
const pendingEvents = [];

/** Initialize Amplitude at app startup. Idempotent. */
export function initAnalytics() {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const key = process.env.REACT_APP_AMPLITUDE_API_KEY;
    if (!key) return;

    try {
      amplitude = await import('@amplitude/unified');
      await amplitude.initAll(key, {
        analytics: {
          autocapture: {
            elementInteractions: true,
            pageViews: true,
            sessions: true,
            formInteractions: true
          },
          fetchRemoteConfig: true
        },
        engagement: {}
      });
      analyticsEnabled = true;

      if (pendingIdentify) {
        const user = pendingIdentify;
        pendingIdentify = null;
        identifyUser(user);
      }
      while (pendingEvents.length > 0) {
        const ev = pendingEvents.shift();
        amplitude.track(ev.name, ev.properties);
      }
    } catch (err) {
      pendingIdentify = null;
      pendingEvents.length = 0;
      initPromise = null;
      throw err;
    }
  })();

  return initPromise;
}

/** Identify the current user after login. */
export function identifyUser({ me, storeChain } = {}) {
  if (!me?.email) return;

  if (!analyticsEnabled || !amplitude) {
    pendingIdentify = { me, storeChain };
    return;
  }

  const identifyEvent = new amplitude.Identify();
  if (me.username) identifyEvent.set('username', me.username);
  if (me.firstName) identifyEvent.set('firstName', me.firstName);
  if (me.lastName) identifyEvent.set('lastName', me.lastName);
  if (storeChain?.dbId) identifyEvent.set('chainId', storeChain.dbId);
  if (storeChain?.name) identifyEvent.set('chainName', storeChain.name);

  amplitude.setUserId(me.email);
  amplitude.identify(identifyEvent);

  if (storeChain?.dbId) {
    amplitude.setGroup('chain', String(storeChain.dbId));
  }
}

/** Clear user identity on logout. */
export function resetAnalytics() {
  pendingIdentify = null;
  pendingEvents.length = 0;
  if (!analyticsEnabled || !amplitude) return;
  amplitude.reset();
}

/** Track a custom event. Buffered until amplitude finishes loading. */
export function trackEvent(name, properties) {
  if (!analyticsEnabled || !amplitude) {
    if (pendingEvents.length < PENDING_EVENT_LIMIT) {
      pendingEvents.push({ name, properties });
    }
    return;
  }
  amplitude.track(name, properties);
}
